import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import usePageTitle from '../../hooks/usePageTitle';
import '../../styles/fonts.css';

const inp = {
  background: '#21262d',
  border: '1px solid #30363d',
  borderRadius: '6px',
  color: '#e6edf3',
  padding: '8px 12px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const lbl = { fontSize: '12px', color: '#8b949e', marginBottom: '4px', display: 'block' };

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={lbl}>{label}</label>
      {children}
    </div>
  );
}

// aspect ratios: [label, width, height]
const ASPECT_RATIOS = [
  ['16:9', 16, 9],
  ['16:10', 16, 10],
  ['21:9', 21, 9],
  ['32:9', 32, 9],
];

// Convert diagonal (inches) + aspect ratio to physical width (cm)
function diagonalToWidthCm(diagonalInch, aspectW, aspectH) {
  const diag = diagonalInch * 2.54; // cm
  return diag * (aspectW / Math.sqrt(aspectW ** 2 + aspectH ** 2));
}

// FOV calculation (flat-screen formula)
function calcFOV(widthCm, distanceCm) {
  return 2 * Math.atan(widthCm / 2 / distanceCm) * (180 / Math.PI);
}

function FovCalculator() {
  const { t } = useTranslation();
  usePageTitle(t('tools.fov.title'));

  const [inputMode, setInputMode] = useState('diagonal'); // 'diagonal' or 'width'
  const [diagonal, setDiagonal] = useState('');
  const [widthCm, setWidthCm] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [customAW, setCustomAW] = useState('');
  const [customAH, setCustomAH] = useState('');
  const [distance, setDistance] = useState('');
  const [numScreens, setNumScreens] = useState('1');
  const [curvature, setCurvature] = useState(''); // radius in mm, empty = flat

  const result = useMemo(() => {
    const dist = parseFloat(distance);
    if (!dist || dist <= 0) return null;

    // Resolve aspect ratio
    let aw, ah;
    if (aspectRatio === 'custom') {
      aw = parseFloat(customAW);
      ah = parseFloat(customAH);
      if (!aw || !ah || aw <= 0 || ah <= 0) return null;
    } else {
      const parts = aspectRatio.split(':');
      aw = parseInt(parts[0]);
      ah = parseInt(parts[1]);
    }

    // Resolve screen width
    let sw;
    if (inputMode === 'diagonal') {
      const diag = parseFloat(diagonal);
      if (!diag || diag <= 0) return null;
      sw = diagonalToWidthCm(diag, aw, ah);
    } else {
      sw = parseFloat(widthCm);
      if (!sw || sw <= 0) return null;
    }

    const screens = parseInt(numScreens) || 1;
    const totalWidth = sw * screens;
    const fov = calcFOV(totalWidth, dist);
    const curv = parseFloat(curvature);
    const idealDist = (curv > 0) ? (curv / 10) : null; // mm → cm

    return { fov, screenWidthCm: sw, totalWidthCm: totalWidth, idealDist };
  }, [inputMode, diagonal, widthCm, aspectRatio, customAW, customAH, distance, numScreens, curvature]);

  const fovWarning = result && result.fov > 150;
  const fovColor = result
    ? (result.fov > 150 ? '#f85149' : result.fov >= 30 ? '#3fb950' : '#e3b341')
    : '#e6edf3';

  return (
    <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <main style={{ flex: 1, maxWidth: '960px', margin: '0 auto', padding: '48px 24px', color: '#c9d1d9' }}>

        <div style={{ marginBottom: '12px' }}>
          <Link to="/tools" style={{ fontSize: '13px', color: '#8b949e', textDecoration: 'none' }}>
            ← {t('tools.pageTitle')}
          </Link>
        </div>
        <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>
          <i className="fas fa-desktop" style={{ color: '#3fb950', marginRight: '10px' }} />
          {t('tools.fov.title')}
        </h1>
        <p style={{ fontSize: '13px', color: '#8b949e', marginBottom: '32px' }}>
          {t('tools.fov.desc')}
        </p>

        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>

          {/* LEFT — inputs */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Input mode toggle */}
              <div>
                <div style={{ fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                  {t('tools.fov.inputMode')}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['diagonal', 'width'].map(m => (
                    <button key={m} onClick={() => setInputMode(m)} style={{
                      padding: '7px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                      border: '1px solid', cursor: 'pointer',
                      borderColor: inputMode === m ? '#3fb950' : '#30363d',
                      background: inputMode === m ? 'rgba(63,185,80,0.12)' : '#21262d',
                      color: inputMode === m ? '#3fb950' : '#8b949e',
                    }}>
                      {t(`tools.fov.inputMode_${m}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Screen size */}
              {inputMode === 'diagonal' ? (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <Field label={t('tools.fov.diagonal')}>
                    <input
                      type="number" min="0" step="0.1"
                      value={diagonal} onChange={e => setDiagonal(e.target.value)}
                      placeholder="ex: 27"
                      style={{ ...inp, width: '100px' }}
                    />
                  </Field>
                  <Field label={t('tools.fov.aspectRatio')}>
                    <select
                      value={aspectRatio}
                      onChange={e => setAspectRatio(e.target.value)}
                      style={{ ...inp, cursor: 'pointer' }}
                    >
                      {ASPECT_RATIOS.map(([label]) => (
                        <option key={label} value={label}>{label}</option>
                      ))}
                      <option value="custom">{t('tools.fov.customAspect')}</option>
                    </select>
                  </Field>
                  {aspectRatio === 'custom' && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <Field label="W">
                        <input type="number" min="1" value={customAW} onChange={e => setCustomAW(e.target.value)} placeholder="16" style={{ ...inp, width: '60px' }} />
                      </Field>
                      <span style={{ paddingBottom: '10px', color: '#8b949e' }}>:</span>
                      <Field label="H">
                        <input type="number" min="1" value={customAH} onChange={e => setCustomAH(e.target.value)} placeholder="9" style={{ ...inp, width: '60px' }} />
                      </Field>
                    </div>
                  )}
                </div>
              ) : (
                <Field label={`${t('tools.fov.widthCm')} (cm)`}>
                  <input
                    type="number" min="0" step="0.1"
                    value={widthCm} onChange={e => setWidthCm(e.target.value)}
                    placeholder="ex: 60.5"
                    style={{ ...inp, width: '120px' }}
                  />
                </Field>
              )}

              {/* Distance */}
              <Field label={`${t('tools.fov.distance')} (cm)`}>
                <input
                  type="number" min="1" step="1"
                  value={distance} onChange={e => setDistance(e.target.value)}
                  placeholder="ex: 65"
                  style={{ ...inp, width: '120px' }}
                />
              </Field>

              {/* Number of screens */}
              <Field label={t('tools.fov.numScreens')}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['1', '2', '3'].map(n => (
                    <button key={n} onClick={() => setNumScreens(n)} style={{
                      width: '48px', padding: '8px', borderRadius: '6px', fontSize: '14px', fontWeight: 700,
                      border: '1px solid', cursor: 'pointer',
                      borderColor: numScreens === n ? '#3fb950' : '#30363d',
                      background: numScreens === n ? 'rgba(63,185,80,0.12)' : '#21262d',
                      color: numScreens === n ? '#3fb950' : '#8b949e',
                    }}>{n}</button>
                  ))}
                </div>
              </Field>

              {/* Curvature */}
              <Field label={t('tools.fov.curvature')}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="number" min="0" step="100"
                    value={curvature} onChange={e => setCurvature(e.target.value)}
                    placeholder={t('tools.fov.curvaturePlaceholder')}
                    style={{ ...inp, width: '140px' }}
                  />
                  <span style={{ fontSize: '12px', color: '#8b949e' }}>
                    {curvature ? `${curvature}R` : t('tools.fov.flatScreen')}
                  </span>
                </div>
                <span style={{ fontSize: '11px', color: '#8b949e', marginTop: '2px' }}>
                  {t('tools.fov.curvatureHint')}
                </span>
              </Field>
            </div>
          </div>

          {/* RIGHT — result card (always visible) */}
          <div style={{ width: '260px', flexShrink: 0, position: 'sticky', top: '24px' }}>
            <div className="tools-result-card" style={{
              background: '#161b22',
              border: '1px solid #30363d',
              borderRadius: '10px',
              padding: '28px 24px',
            }}>
              {result ? (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                      {t('tools.fov.result')}
                    </div>
                    <div style={{ fontSize: '64px', fontWeight: 700, color: fovColor, lineHeight: 1 }}>
                      {result.fov.toFixed(1)}°
                    </div>
                    {fovWarning && (
                      <div style={{ fontSize: '12px', color: '#f85149', marginTop: '8px' }}>
                        ⚠ {t('tools.fov.highFovWarning')}
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: '#8b949e', background: '#21262d', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '6px' }} />
                    {t('tools.fov.iracing_note')}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <InfoRow label={t('tools.fov.screenWidthLabel')} value={`${result.screenWidthCm.toFixed(1)} cm`} />
                    {parseInt(numScreens) > 1 && (
                      <InfoRow label={t('tools.fov.totalWidthLabel')} value={`${result.totalWidthCm.toFixed(1)} cm`} />
                    )}
                    {result.idealDist && (
                      <InfoRow
                        label={t('tools.fov.idealDistance')}
                        value={`${result.idealDist.toFixed(0)} cm`}
                        highlight={Math.abs(parseFloat(distance) - result.idealDist) > 15}
                        highlightNote={t('tools.fov.idealDistanceNote')}
                      />
                    )}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <i className="fas fa-desktop" style={{ fontSize: '32px', color: '#30363d', marginBottom: '12px', display: 'block' }} />
                  <div style={{ fontSize: '13px', color: '#8b949e', lineHeight: 1.6 }}>
                    {t('tools.fov.resultPlaceholder')}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

      </main>
      <Footer />
    </div>
  );
}

function InfoRow({ label, value, highlight, highlightNote }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #21262d' }}>
        <span style={{ fontSize: '13px', color: '#8b949e' }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: highlight ? '#e3b341' : '#e6edf3' }}>{value}</span>
      </div>
      {highlight && highlightNote && (
        <div style={{ fontSize: '11px', color: '#e3b341', paddingTop: '2px' }}>⚠ {highlightNote}</div>
      )}
    </div>
  );
}

export default FovCalculator;
